import React, { useEffect, useState } from "react";

import Toggle from "../../components/common/Toggle";
import { useToast } from "../../contexts/ToastContext";
import { authService } from "../../services/auth";
import { organizationsService } from "../../services/organizations";
import { getApiErrorMessage } from "../../utils/axios";

const FEATURES = [
  {
    key: "enable_audits",
    label: "Audits",
    description:
      "Run SEO, UX, and page speed audits, then turn findings into site fixes.",
  },
  {
    key: "enable_branding",
    label: "Branding",
    description:
      "Capture header, footer, and pages from a site you already own so new builds can match that look.",
    staffOnly: true,
  },
  {
    key: "enable_media",
    label: "Media",
    description:
      "Generate and manage images for your sites, including logos, favicons, and content photos.",
    beta: true,
    staffOnly: true,
  },
  {
    key: "enable_forms",
    label: "Forms",
    description:
      "Collect leads and submissions with forms on your sites.",
    beta: true,
    staffOnly: true,
  },
];

const Features = ({ currentUser, setCurrentUser }) => {
  const { error: toastError, success: toastSuccess } = useToast();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const isStaff = Boolean(currentUser?.identity?.is_staff);
  const isOrgAdmin =
    organization?.role === "owner" || organization?.role === "admin";

  useEffect(() => {
    organizationsService.list(
      (res) => {
        const rows = res.data?.results || res.data || [];
        setOrganization(rows[0] || null);
        setLoading(false);
      },
      () => {
        setLoading(false);
        toastError("Could not load organization settings");
      },
    );
  }, [toastError]);

  const canEditFeature = (feature) => {
    if (!isOrgAdmin) return false;
    if (feature.staffOnly && !isStaff) return false;
    return true;
  };

  const toggleFeature = (feature) => {
    if (!organization || !canEditFeature(feature) || savingKey) return;
    const next = !organization[feature.key];
    setSavingKey(feature.key);
    organizationsService.update(
      organization.hash,
      { [feature.key]: next },
      (res) => {
        setOrganization(res.data);
        setSavingKey(null);
        authService.fetchUser((userRes) => {
          if (setCurrentUser) setCurrentUser(userRes.data);
        });
        toastSuccess(`${feature.label} updated`);
      },
      (err) => {
        setSavingKey(null);
        toastError(getApiErrorMessage(err, "Could not update feature"));
      },
    );
  };

  if (loading) {
    return (
      <div className="max-w-2xl text-sm opacity-60">Loading features...</div>
    );
  }

  if (!organization) {
    return (
      <div className="max-w-2xl text-sm opacity-60">
        No organization found for this account.
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-8">
      <section>
        <h2 className="mb-1 text-sm font-semibold">Features</h2>
        <p className="mb-4 text-xs opacity-60">
          {isOrgAdmin
            ? "Turn optional capabilities on or off for your organization."
            : "Only organization owners and admins can change these settings."}
        </p>
        <div className="space-y-3">
          {FEATURES.map((feature) => {
            const checked = Boolean(organization[feature.key]);
            const editable = canEditFeature(feature);
            const disabled = !editable || savingKey === feature.key;
            return (
              <div
                key={feature.key}
                className="rounded-xl border border-theme-secondary p-5"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{feature.label}</p>
                      {feature.beta && (
                        <span className="rounded bg-theme-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-theme-text opacity-70">
                          Beta
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs opacity-60">{feature.description}</p>
                  </div>
                  <Toggle
                    checked={checked}
                    disabled={disabled}
                    label={feature.label}
                    onChange={() => toggleFeature(feature)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Features;
